import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  fetchUserListing,
  fetchUserConversations,
  setSelectedUserId,
} from 'src/redux/chatbotConversations'
import { formatDate, useDebouncedFilter } from './helpers'

const UserTable = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { users, usersLoading, usersTotalCount, selectedUserId } = useSelector(
    state => state.chatbotConversations,
  )

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const { filterValue: emailFilter, handleFilterChange: handleEmailFilterChange } =
    useDebouncedFilter(value => {
      setPage(0)
      dispatch(fetchUserListing({ userEmail: value || undefined, page: 1, pageSize: rowsPerPage }))
    })

  const handleRowClick = userId => {
    dispatch(setSelectedUserId(userId))
    dispatch(fetchUserConversations(userId))
  }

  const handlePageChange = (_event, newPage) => {
    setPage(newPage)
    dispatch(
      fetchUserListing({
        userEmail: emailFilter || undefined,
        page: newPage + 1,
        pageSize: rowsPerPage,
      }),
    )
  }

  const handleRowsPerPageChange = event => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    dispatch(
      fetchUserListing({
        userEmail: emailFilter || undefined,
        page: 1,
        pageSize: newRowsPerPage,
      }),
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.grey[200]}`, flexShrink: 0 }}>
        <TextField
          label='Filter by email'
          value={emailFilter}
          onChange={handleEmailFilterChange}
          size='small'
          fullWidth
        />
      </Box>

      {usersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User Email</TableCell>
                <TableCell align='right'>Conversations</TableCell>
                <TableCell align='right'>Messages</TableCell>
                <TableCell align='right'>Feedback</TableCell>
                <TableCell>Last Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow
                  key={user.userId}
                  hover
                  onClick={() => handleRowClick(user.userId)}
                  selected={user.userId === selectedUserId}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{user.userEmail}</TableCell>
                  <TableCell align='right'>{user.conversationCount}</TableCell>
                  <TableCell align='right'>{user.totalMessageCount}</TableCell>
                  <TableCell align='right'>{user.totalFeedbackCount}</TableCell>
                  <TableCell>
                    <Typography variant='caption' noWrap>
                      {formatDate(user.lastActiveAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    <Typography variant='body2' color='textSecondary'>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <TablePagination
        component='div'
        count={usersTotalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'grey.200' }}
      />
    </Box>
  )
}

export default UserTable
